/* SVAICO homepage catalogue + Fitment v8.1 vehicle filter (V20 smart model selector).
   CUSTOMER FLOW: Make -> Year -> Model / chassis -> Engine power.
   R2 FITMENT STORAGE IS UNCHANGED: make/model/year/hp. V18 adds an OPTIONAL tiny server-side
   year index at catalog/year-index/<make_id>.json. The theme requests that file in parallel
   with catalog/makes/<make_id>.json, so Make -> Year becomes effectively instant when the
   pipeline publishes the index. Older R2 data still works: embedded year metadata, browser
   cache, then bounded model-index scanning are used as fallbacks. Vehicle fitment stays
   R2-authoritative.

   V20 UX: Model stays after Year, but the dropdown now separates model name from
   generation/chassis and derives the model's full available year span from the already-loaded
   year index. No extra R2 requests are needed for these labels.

   FUTURE MAINTENANCE: main selector handlers are onMake(), onYear(), onModel(), onPower().
   Keep restoreSavedVehicle() and resetAfter() in the same order if the selector flow changes.
   Full-catalogue categories/subgroups/counts still come from catalog-counts.json. */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-r2-vehicle-picker]';
  const SHOPIFY_COUNT_CAP_SIGNAL = 25001;
  const text = (value) => String(value ?? '');
  const escapeHtml = (value) => text(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[char]);
  const cleanUrl = (value) => text(value).trim().replace(/\/+$/, '');
  const normalize = (value) => text(value)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const normalizeSearch = (value) => text(value)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase().trim();
  const handleize = (value) => text(value)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const CATEGORY_VISUALS = {
    brakes: {
      aliases: ['brake system', 'brakes'],
      description: 'Discs, pads, calipers and brake hydraulics',
      svg: '<circle cx="32" cy="32" r="21"/><circle cx="32" cy="32" r="7"/><path d="M45 18c6 4 9 10 9 17 0 5-1 9-4 13l-8-5c2-3 3-6 3-9 0-4-2-8-5-10z"/>'
    },
    filters: {
      aliases: ['filters', 'filter'],
      description: 'Oil, air, fuel, hydraulic and cabin filters',
      svg: '<path d="M12 15h40l-14 16v16l-12 5V31z"/><path d="M18 22h28"/>'
    },
    suspension: {
      aliases: ['suspension', 'damping'],
      description: 'Shocks, springs, arms, bearings and mounts',
      svg: '<path d="M22 9v9l-6 5 12 8-12 8 12 8-6 5v4"/><path d="M42 9v46"/><path d="M36 15h12M36 49h12"/>'
    },
    steering: {
      aliases: ['steering'],
      description: 'Tie rods, racks, pumps and steering joints',
      svg: '<circle cx="32" cy="32" r="22"/><circle cx="32" cy="32" r="6"/><path d="M11 29h42M32 38v16"/>'
    },
    wipers: {
      aliases: ['wipers and washers', 'wiper', 'windscreen cleaning'],
      description: 'Wiper blades, motors, pumps and washers',
      svg: '<path d="M9 43c12-18 34-25 46-19"/><path d="M17 46l25-22"/><path d="M27 47l22-19"/>'
    },
    engine: {
      aliases: ['engine parts', 'engine', 'belt drive', 'timing'],
      description: 'Timing, internal engine and air intake parts',
      svg: '<path d="M13 23h8l5-7h16l5 7h7v24H13z"/><path d="M19 30h8v8h-8zM36 30h8v8h-8z"/><path d="M8 29h5M54 29h4"/>'
    },
    fuel: {
      aliases: ['fuel system', 'fuel preparation', 'mixture formation'],
      description: 'Pumps, injectors, rails, tanks and fuel sensors',
      svg: '<path d="M16 11h25v42H16z"/><path d="M21 17h15v12H21z"/><path d="M41 20h6l5 6v21c0 5-7 5-7 0V34"/><path d="M20 46h17"/>'
    },
    exhaust: {
      aliases: ['exhaust system', 'exhaust'],
      description: 'DPF, catalysts, pipes, sensors and silencers',
      svg: '<path d="M8 38h27l8-8h13v12H42l-7-4H8z"/><path d="M48 24c2-3 5-4 8-3M45 18c3-4 8-6 12-4"/>'
    },
    electric: {
      aliases: ['electrical system', 'electrics', 'electric', 'ignition', 'lighting'],
      description: 'Alternators, starters, sensors and lighting',
      svg: '<rect x="12" y="18" width="40" height="30" rx="4"/><path d="M24 18v-5h16v5M28 25l-6 12h9l-3 9 14-17h-9l3-4z"/>'
    },
    cooling: {
      aliases: ['engine cooling', 'cooling system', 'cooling', 'heating and ventilation', 'air conditioning'],
      description: 'Radiators, pumps, thermostats and cooling parts',
      svg: '<rect x="13" y="12" width="38" height="40" rx="3"/><path d="M20 17v30M27 17v30M34 17v30M41 17v30M48 17v30"/><path d="M8 22h5M51 42h5"/>'
    },
    transmission: {
      aliases: ['transmission and clutch', 'transmission', 'clutch', 'gearbox', 'wheel drive'],
      description: 'Clutch, flywheels, gearbox and driveline parts',
      svg: '<circle cx="22" cy="32" r="10"/><circle cx="43" cy="22" r="8"/><circle cx="43" cy="43" r="8"/><path d="M30 28l6-3M30 36l6 4"/>'
    },
    body: {
      aliases: ['body and lighting', 'body', 'body parts', 'interior', 'comfort systems'],
      description: 'Body parts, mirrors, lamps and exterior lighting',
      svg: '<path d="M8 39l6-14h25l10 8h7v13H8z"/><circle cx="20" cy="46" r="5"/><circle cx="46" cy="46" r="5"/><path d="M19 25l5 8h20"/>'
    },
    generic: {
      aliases: [],
      description: 'Replacement parts available from the SVAICO catalogue',
      svg: '<path d="M17 11h30v42H17z"/><path d="M23 19h18M23 28h18M23 37h12"/>'
    }
  };

  const categoryVisual = (name) => {
    const key = normalize(name);

    // V36: exact category aliases must win before broad substring aliases.
    // Without this, "Engine cooling" matched the broad "engine" alias and
    // "Body and lighting" matched the broad "lighting" alias first.
    for (const [id, visual] of Object.entries(CATEGORY_VISUALS)) {
      if (id === 'generic') continue;
      if (visual.aliases.some((alias) => key === alias)) return { id, ...visual };
    }

    // Fallback for source categories that contain extra wording: choose the
    // longest matching alias, which is more specific than a short generic term.
    let best = null;
    let bestAliasLength = -1;
    for (const [id, visual] of Object.entries(CATEGORY_VISUALS)) {
      if (id === 'generic') continue;
      for (const alias of visual.aliases) {
        if (key.includes(alias) && alias.length > bestAliasLength) {
          best = { id, ...visual };
          bestAliasLength = alias.length;
        }
      }
    }
    return best || { id: 'generic', ...CATEGORY_VISUALS.generic };
  };

  const placeholderFor = (subgroupName, productTitle = '') => {
    const assets = window.SVAICO_PLACEHOLDER_ASSETS || {};
    const value = normalize(`${subgroupName || ''} ${productTitle || ''}`);
    let key = 'generic';
    if (value.includes('timing system') || value.includes('timing belt') || value.includes('timing kit') || value.includes('timing chain')) key = 'timing';
    else if (value.includes('fuel injector') || /(^| )injector( |$)/.test(value)) key = 'injector';
    else if (value.includes('brake disc') || value.includes('brake rotor')) key = 'brake';
    else if (value.includes('shock absorber')) key = 'shock';
    else if (value.includes('oil filter')) key = 'oilFilter';
    else if (value.includes('spark plug') || value.includes('ignition system')) key = 'sparkPlug';
    else if (value.includes('radiator')) key = 'radiator';
    else if (value.includes('crankshaft and pistons') || value.includes('crankshaft bearing') || value.includes('main bearing') || value.includes('connecting rod bearing') || value.includes('conrod bearing') || value.includes('crankshaft')) key = 'engine';
    return assets[key] || assets.generic || '';
  };

  class SearchableVehicleSelect {
    constructor(select) {
      this.select = select;
      this.field = select.closest('.vehicle-field');
      this.placeholder = '';
      this.options = [];
      this.filtered = [];
      this.filteredTotal = 0;
      this.activeIndex = -1;
      this.isOpen = false;
      this.listId = `${select.id || `r2-search-${Math.random().toString(36).slice(2)}`}-listbox`;
      this.wrapper = document.createElement('div');
      this.wrapper.className = 'r2-search-select';
      this.wrapper.innerHTML = `
        <input class="r2-search-select__input" type="text" autocomplete="off" spellcheck="false"
          role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="${escapeHtml(this.listId)}">
        <button class="r2-search-select__toggle" type="button" tabindex="-1" aria-label="Open options">⌄</button>
        <div class="r2-search-select__menu" id="${escapeHtml(this.listId)}" role="listbox" hidden></div>`;
      this.input = this.wrapper.querySelector('.r2-search-select__input');
      this.toggle = this.wrapper.querySelector('.r2-search-select__toggle');
      this.menu = this.wrapper.querySelector('.r2-search-select__menu');
      this.select.classList.add('r2-native-select');
      this.field?.classList.add('has-r2-search');
      this.select.insertAdjacentElement('afterend', this.wrapper);
      this.bind();
      this.sync();
    }

    bind() {
      this.input.addEventListener('focus', () => {
        if (this.input.disabled) return;
        this.input.select();
        this.open('');
      });
      this.input.addEventListener('click', () => {
        if (!this.input.disabled) this.open(this.input.selectionStart === 0 && this.input.selectionEnd === this.input.value.length ? '' : this.input.value);
      });
      this.input.addEventListener('input', () => {
        const selected = this.selectedLabel();
        if (this.select.value && this.input.value !== selected) {
          this.select.value = '';
          this.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.open(this.input.value);
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.input.addEventListener('blur', () => {
        window.setTimeout(() => {
          if (!this.wrapper.contains(document.activeElement)) {
            if (!this.select.value) this.input.value = '';
            this.close();
          }
        }, 120);
      });
      this.toggle.addEventListener('mousedown', (event) => event.preventDefault());
      this.toggle.addEventListener('click', () => {
        if (this.input.disabled) return;
        this.input.focus();
        this.input.select();
        this.open('');
      });
      this.menu.addEventListener('mousedown', (event) => event.preventDefault());
      this.menu.addEventListener('click', (event) => {
        const option = event.target.closest('[data-r2-search-value]');
        if (option) this.choose(option.dataset.r2SearchValue || '');
      });
      document.addEventListener('mousedown', (event) => {
        if (!this.wrapper.contains(event.target)) this.close();
      });
    }

    sync() {
      const selectOptions = Array.from(this.select.options);
      this.placeholder = selectOptions[0]?.textContent?.trim() || 'Select';
      this.options = selectOptions.slice(1).map((option) => {
        const label = option.textContent?.trim() || option.value;
        const primary = option.dataset.r2Primary?.trim() || label;
        const secondary = option.dataset.r2Secondary?.trim() || '';
        const display = option.dataset.r2Display?.trim() || label;
        return {
          value: option.value,
          label,
          primary,
          secondary,
          display,
          primarySearch: normalizeSearch(primary),
          search: normalizeSearch([label, primary, secondary, display].filter(Boolean).join(' '))
        };
      });
      this.input.placeholder = this.placeholder;
      this.input.disabled = this.select.disabled;
      this.toggle.disabled = this.select.disabled;
      this.wrapper.classList.toggle('is-disabled', this.select.disabled);
      this.input.value = this.selectedLabel();
      if (this.select.disabled) this.close();
      else if (this.isOpen) this.open(this.input.value);
    }

    selectedLabel() {
      if (!this.select.value) return '';
      const option = this.select.options[this.select.selectedIndex];
      return option?.dataset.r2Display?.trim() || option?.textContent?.trim() || '';
    }

    open(query = '') {
      if (this.select.disabled) return;
      const needle = normalizeSearch(query);
      const starts = [];
      const contains = [];
      for (const option of this.options) {
        if (!needle || option.primarySearch.startsWith(needle) || option.search.startsWith(needle)) starts.push(option);
        else if (option.search.includes(needle)) contains.push(option);
      }
      const matches = [...starts, ...contains];
      const limit = this.select.matches('[data-r2-model]') ? 250 : 100;
      this.filteredTotal = matches.length;
      this.filtered = matches.slice(0, limit);
      this.activeIndex = this.filtered.length ? 0 : -1;
      this.render();
      this.menu.hidden = false;
      this.isOpen = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.updateActive();
    }

    render() {
      if (!this.filtered.length) {
        this.menu.innerHTML = '<div class="r2-search-select__empty">No matches</div>';
        return;
      }
      const rows = this.filtered.map((option, index) => {
        const content = option.secondary
          ? `<span class="r2-search-select__primary">${escapeHtml(option.primary)}</span><span class="r2-search-select__secondary">${escapeHtml(option.secondary)}</span>`
          : `<span class="r2-search-select__primary">${escapeHtml(option.primary)}</span>`;
        return `
          <button class="r2-search-select__option${option.secondary ? ' r2-search-select__option--detailed' : ''}" type="button" role="option"
            id="${escapeHtml(this.listId)}-${index}" data-r2-search-value="${escapeHtml(option.value)}"
            aria-selected="${option.value === this.select.value ? 'true' : 'false'}">
            ${content}
          </button>`;
      }).join('');
      const more = this.filteredTotal > this.filtered.length
        ? `<div class="r2-search-select__more">${(this.filteredTotal - this.filtered.length).toLocaleString()} more matches — type to narrow the list.</div>`
        : '';
      this.menu.innerHTML = rows + more;
    }

    close() {
      this.menu.hidden = true;
      this.isOpen = false;
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    choose(value) {
      this.select.value = value;
      this.input.value = this.selectedLabel();
      this.close();
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.input.value = this.selectedLabel();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.isOpen) this.open(this.input.value);
        if (!this.filtered.length) return;
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + direction + this.filtered.length) % this.filtered.length;
        this.updateActive();
        return;
      }
      if (event.key === 'Enter' && this.isOpen && this.activeIndex >= 0) {
        event.preventDefault();
        this.choose(this.filtered[this.activeIndex].value);
      }
    }

    updateActive() {
      const nodes = Array.from(this.menu.querySelectorAll('[data-r2-search-value]'));
      nodes.forEach((node, index) => node.classList.toggle('is-active', index === this.activeIndex));
      const active = nodes[this.activeIndex];
      if (active) {
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }
  }

  class R2VehiclePicker {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.baseUrl = cleanUrl(this.config.fitmentBaseUrl);
      this.catalogCountsUrl = text(this.config.catalogCountsUrl || '').trim();
      this.catalogCountsStorageKey = `svaicoCatalogCountsV1:${this.catalogCountsUrl || 'default'}`;
      this.version = '';
      this.state = {
        makeId: '', modelId: '', year: '', hp: '', leaf: null,
        category: null, subgroup: null, makeCatalog: null, modelData: null,
        yearModels: new Map()
      };
      // V18: model indexes inspected during fallback year discovery are reused later.
      this.modelDataCache = new Map();
      this.restoring = false;
      // V28 MOBILE HISTORY: category -> subgroup -> products are genuine
      // same-document history steps. Native browser Back / iOS-Android swipe-back
      // can therefore move up the catalogue hierarchy before leaving SVAICO.
      this.catalogHistoryField = 'svaicoCatalogV1';
      this.countCache = new Map();
      this.countCapped = new Set();
      this.categoryCountExact = new Map();
      this.catalogTotal = Number(this.config.catalogTotal || 0);
      this.catalogTotalExact = !Boolean(this.config.catalogTotalCapped) && this.catalogTotal < SHOPIFY_COUNT_CAP_SIGNAL;
      this.catalogCountsReady = false;
      this.catalogCountsGeneratedAt = '';
      this.productMode = 'catalog';
      this.master = this.masterCatalog();

      this.make = root.querySelector('[data-r2-make]');
      this.model = root.querySelector('[data-r2-model]');
      this.year = root.querySelector('[data-r2-year]');
      this.power = root.querySelector('[data-r2-power]');
      this.form = root.querySelector('[data-r2-form]');
      this.reset = root.querySelector('[data-r2-reset]');
      this.status = root.querySelector('[data-r2-status]');
      this.summary = root.querySelector('[data-r2-catalog-summary]');
      this.clearFilter = root.querySelector('[data-r2-clear-filter]');
      this.categories = root.querySelector('[data-r2-categories]');
      this.categoryGrid = root.querySelector('[data-r2-category-grid]');
      this.subgroups = root.querySelector('[data-r2-subgroups]');
      this.subgroupGrid = root.querySelector('[data-r2-subgroup-grid]');
      this.subgroupHeading = root.querySelector('[data-r2-subgroup-heading]');
      this.results = root.querySelector('[data-r2-results]');
      this.resultGrid = root.querySelector('[data-r2-result-grid]');
      this.resultTitle = root.querySelector('[data-r2-result-title]');
      this.resultEyebrow = root.querySelector('[data-r2-result-eyebrow]');
      this.resultStatus = root.querySelector('[data-r2-result-status]');
      this.pagination = root.querySelector('[data-r2-pagination]');
      this.backCategories = root.querySelector('[data-r2-back-categories]');
      this.backSubgroups = root.querySelector('[data-r2-back-subgroups]');
      this.searchableSelects = new Map();
      [this.make, this.model, this.year, this.power].forEach((select) => {
        if (select) this.searchableSelects.set(select, new SearchableVehicleSelect(select));
      });
      this.bind();
      this.initialize();
    }

    masterCatalog() {
      const source = window.SVAICO_SUBGROUPS || {};
      const names = Object.keys(source);
      return names.map((name) => ({
        name,
        key: normalize(name),
        tagHandle: handleize(name),
        subgroups: (source[name] || []).map((item) => ({
          name: text(item?.[0]),
          description: text(item?.[1]),
          icon: text(item?.[2]),
          key: normalize(item?.[0]),
          tagHandle: handleize(item?.[0])
        }))
      }));
    }

    categoryCountKey(categoryName) {
      return `category:${normalize(categoryName)}`;
    }

    subgroupCountKey(categoryName, subgroupName) {
      return `subgroup:${normalize(categoryName)}|${normalize(subgroupName)}`;
    }

    setCategoryCount(categoryName, value) {
      this.countCache.set(this.categoryCountKey(categoryName), Number(value));
    }

    getCategoryCount(categoryName) {
      return this.countCache.get(this.categoryCountKey(categoryName));
    }

    setSubgroupCount(categoryName, subgroupName, value) {
      this.countCache.set(this.subgroupCountKey(categoryName, subgroupName), Number(value));
    }

    getSubgroupCount(categoryName, subgroupName) {
      return this.countCache.get(this.subgroupCountKey(categoryName, subgroupName));
    }

    syncMasterCatalogFromCounts(payload) {
      const categories = payload?.categories;
      if (!categories || typeof categories !== 'object') return false;

      const previous = Array.isArray(this.master) ? this.master : [];
      const previousCategories = new Map(previous.map((category) => [category.key, category]));
      const next = [];

      for (const [categoryNameRaw, categoryValue] of Object.entries(categories)) {
        const categoryName = text(categoryNameRaw).trim();
        if (!categoryName) continue;
        const categoryKey = normalize(categoryName);
        const oldCategory = previousCategories.get(categoryKey);
        const subgroupValues = categoryValue && typeof categoryValue === 'object' && categoryValue.subgroups && typeof categoryValue.subgroups === 'object'
          ? categoryValue.subgroups : {};
        const oldSubgroups = new Map((oldCategory?.subgroups || []).map((subgroup) => [subgroup.key, subgroup]));

        const subgroups = Object.entries(subgroupValues).map(([subgroupNameRaw, subgroupValue]) => {
          const subgroupName = text(subgroupNameRaw).trim();
          const subgroupKey = normalize(subgroupName);
          const oldSubgroup = oldSubgroups.get(subgroupKey);
          const count = this.catalogCountValue(subgroupValue);
          return {
            name: subgroupName,
            description: text(oldSubgroup?.description || ''),
            icon: text(oldSubgroup?.icon || ''),
            key: subgroupKey,
            tagHandle: handleize(subgroupName),
            catalogCount: count
          };
        }).filter((subgroup) => subgroup.name && (!Number.isFinite(subgroup.catalogCount) || subgroup.catalogCount > 0));

        const categoryCount = this.catalogCountValue(categoryValue);
        if (Number.isFinite(categoryCount) && categoryCount <= 0) continue;

        next.push({
          name: categoryName,
          key: categoryKey,
          tagHandle: handleize(categoryName),
          subgroups
        });
      }

      if (!next.length) return false;
      this.master = next;
      return true;
    }

    readConfig() {
      const node = this.root.querySelector('[data-r2-config]');
      try { return JSON.parse(node?.textContent || '{}'); }
      catch (error) { console.error('Invalid R2 vehicle-picker config', error); return {}; }
    }

    bind() {
      this.make?.addEventListener('change', () => this.onMake());
      this.model?.addEventListener('change', () => this.onModel());
      this.year?.addEventListener('change', () => this.onYear());
      this.power?.addEventListener('change', () => this.onPower());
      this.form?.addEventListener('submit', (event) => { event.preventDefault(); this.applyVehicleFilter({ scroll: true }); });
      this.reset?.addEventListener('click', () => this.resetAll());
      this.clearFilter?.addEventListener('click', () => this.resetAll());
      // V26 SEO: category/subgroup cards are real crawlable links. Normal clicks
      // keep the fast in-page catalogue UX; modifier clicks may open the fallback
      // Shopify collection URL in a new tab like any ordinary link.
      this.categoryGrid?.addEventListener('click', (event) => {
        const card = event.target.closest('[data-r2-category]');
        if (!card || card.disabled || card.getAttribute('aria-disabled') === 'true') return;
        if (card.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
        event.preventDefault();
        this.selectCategory(card.dataset.r2Category || '');
      });
      this.subgroupGrid?.addEventListener('click', (event) => {
        const card = event.target.closest('[data-r2-subgroup]');
        if (!card || card.disabled || card.getAttribute('aria-disabled') === 'true') return;
        if (card.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;

        // V38 MOBILE CATALOGUE: phones use the subgroup card as a real page
        // transition. This gives the product list its own URL/page instead of
        // expanding it underneath the subgroup cards. Desktop keeps the exact
        // V37 in-page catalogue behaviour.
        if (window.matchMedia('(max-width: 749px)').matches && card.tagName === 'A') {
          this.rememberCatalogPosition();

          // V39: use a dedicated lightweight mobile collection view. The
          // normal collection template can hit Shopify's internal Liquid
          // rendering limit on very large subgroups. This alternate view
          // loads the already proven 36-product inline fragment instead, so
          // phones get a real standalone page without touching desktop.
          const target = new URL(card.href, window.location.origin);
          target.searchParams.set('view', 'svaico-mobile');
          if (this.state.category?.master?.name) {
            target.searchParams.set('svaico_category', this.state.category.master.name);
          }
          const subgroup = this.state.category?.master?.subgroups?.find(
            (item) => item.key === (card.dataset.r2Subgroup || '')
          );
          if (subgroup?.name) target.searchParams.set('svaico_subgroup', subgroup.name);

          // V41 MOBILE FITMENT HANDOFF: when a vehicle is selected, the counts
          // shown on the homepage come from the authoritative R2 fitment set.
          // The standalone phone page must therefore receive that SAME subgroup
          // instead of falling back to Shopify's all-vehicle tag collection.
          if (this.state.leaf && subgroup) {
            const fitmentSubgroup = this.fitmentSubgroup(this.state.category?.r2, subgroup);
            if (fitmentSubgroup) {
              const setId = this.state.modelData?.routes?.[String(this.state.year)]?.[String(this.state.hp)] || '';
              const total = Number(fitmentSubgroup.total ?? (Array.isArray(fitmentSubgroup.products) ? fitmentSubgroup.products.length : 0));
              // The URL is the durable handoff. Do not make fitment mode depend on
              // an in-memory setId being present at tap time.
              target.searchParams.set('svaico_fitment', '1');
              if (this.state.makeId) target.searchParams.set('svaico_make_id', this.state.makeId);
              if (this.state.modelId) target.searchParams.set('svaico_model_id', this.state.modelId);
              if (this.state.year) target.searchParams.set('svaico_year', this.state.year);
              if (this.state.hp) target.searchParams.set('svaico_hp', this.state.hp);
              if (Number.isFinite(total)) target.searchParams.set('svaico_fitment_total', String(total));

              // Fast path: preserve the already loaded exact subgroup in session.
              // If this is unavailable/incomplete, the destination rebuilds from R2
              // using the query-string vehicle identity above.
              if (setId) {
                const context = {
                  schema: 'svaico-mobile-fitment-v1',
                  savedAt: Date.now(),
                  baseUrl: this.baseUrl,
                  version: this.version,
                  makeId: this.state.makeId,
                  modelId: this.state.modelId,
                  year: this.state.year,
                  hp: this.state.hp,
                  setId,
                  setStorage: this.state.modelData?.set_storage?.[setId] || null,
                  categoryName: this.state.category?.master?.name || '',
                  subgroupName: subgroup.name,
                  subgroup: fitmentSubgroup,
                  total,
                  vehicleLabel: this.selectedVehicleLabel()
                };
                try { sessionStorage.setItem('svaicoMobileFitmentContextV41', JSON.stringify(context)); } catch (_) {}
              }
            }
          }
          window.location.assign(target.toString());
          return;
        }

        event.preventDefault();
        this.selectSubgroup(card.dataset.r2Subgroup || '');
      });
      this.pagination?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-r2-page]');
        if (button && !button.disabled) {
          this.loadProducts(Number(button.dataset.r2Page || 1), { pagination: true });
        }
      });
      // V37 RETURN-TO-CATALOG: save the exact catalogue position immediately
      // before leaving for a product page. If the homepage is reloaded when the
      // customer presses Back, initialize() can rebuild the category/subgroup/page
      // and then return to the same scroll position instead of showing Categories.
      this.resultGrid?.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (!link || event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        this.rememberCatalogPosition();
      });
      this.backCategories?.addEventListener('click', () => this.closeDetails({ useHistory: true }));
      this.backSubgroups?.addEventListener('click', () => this.showSubgroups({ useHistory: true }));
      window.addEventListener('popstate', (event) => this.restoreCatalogHistory(event));
      window.addEventListener('pagehide', () => this.rememberCatalogPosition());
    }

    async initialize() {
      // Capture the browser-history payload BEFORE restoreSavedVehicle(). Applying
      // a saved vehicle intentionally resets the visible catalogue and replaces
      // history with Categories, so without this snapshot a reload-after-Back
      // loses the previous subgroup/results location.
      const initialCatalogNav = this.catalogHistoryValue();
      this.ensureCatalogHistoryRoot();
      this.applyCachedCatalogCounts();
      this.renderCategories();
      this.loadCatalogCounts();
      if (!this.baseUrl) {
        this.setStatus('Vehicle filtering is temporarily unavailable. You can still browse the full catalogue.', true);
        await this.restoreCatalogHistorySnapshot(initialCatalogNav, { onLoad: true });
        return;
      }
      try {
        this.setStatus('Loading vehicle catalogue…');
        const manifest = await this.fetchJson('manifest.json', false);
        this.version = text(manifest.built_at || manifest.registry_uuid || manifest.schema_version);
        // V41: expose the active R2 runtime to the standalone phone fitment page.
        // This is only a recovery path for refresh/new-tab cases; normal phone
        // navigation also carries a faster sessionStorage handoff.
        try {
          localStorage.setItem('svaicoFitmentRuntimeV41', JSON.stringify({
            baseUrl: this.baseUrl, version: this.version, savedAt: Date.now()
          }));
        } catch (_) {}
        const payload = await this.fetchJson('catalog/makes.json');
        this.fill(this.make, payload.makes || [], 'Select make', (item) => item.id, (item) => item.name);
        const restored = await this.restoreSavedVehicle();
        if (!restored) this.setStatus('Browse all parts below, or select a vehicle to filter the catalogue.');
        await this.restoreCatalogHistorySnapshot(initialCatalogNav, { onLoad: true });
      } catch (error) {
        console.error(error);
        this.setStatus('Vehicle filtering could not be loaded. The full catalogue is still available below.', true);
        await this.restoreCatalogHistorySnapshot(initialCatalogNav, { onLoad: true });
      }
    }

    selectionChanged() {
      if (this.restoring || !this.state.leaf) return;
      this.state.leaf = null;
      this.state.category = null;
      this.state.subgroup = null;
      this.productMode = 'catalog';
      this.renderCategories();
      this.closeDetails({ replaceHistory: true });
      this.updateCatalogSummary();
    }

    // Return the years already embedded in a make-catalog model row. Newer R2
    // catalog builders may expose a list or min/max fields; supporting several names keeps
    // the theme backward/forward compatible without making the pipeline schema a dependency.
    modelYearsFromCatalogItem(item) {
      const years = new Set();
      const add = (value) => {
        const year = Number(value);
        if (Number.isInteger(year) && year >= 1880 && year <= 2100) years.add(year);
      };
      const addList = (value) => {
        if (Array.isArray(value)) value.forEach((entry) => typeof entry === 'object' ? add(entry?.year ?? entry?.value ?? entry?.id) : add(entry));
        else if (value && typeof value === 'object') Object.keys(value).forEach(add);
      };

      ['years', 'available_years', 'model_years', 'year_list'].forEach((key) => addList(item?.[key]));
      if (item?.routes && typeof item.routes === 'object') Object.keys(item.routes).forEach(add);
      add(item?.year);

      const ranges = [
        [item?.year_min, item?.year_max], [item?.min_year, item?.max_year],
        [item?.year_from, item?.year_to], [item?.from_year, item?.to_year],
        [item?.start_year, item?.end_year], [item?.first_year, item?.last_year]
      ];
      for (const [fromRaw, toRaw] of ranges) {
        const from = Number(fromRaw); const to = Number(toRaw);
        if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1880 || to > 2100 || to < from || to - from > 80) continue;
        for (let year = from; year <= to; year++) years.add(year);
      }
      return [...years].sort((a, b) => a - b);
    }

    yearIndexStorageKey() {
      // Keep the V17 cache key so customers who already built a local year map do not
      // have to download the same model indexes again after upgrading to V18.
      return `svaicoVehicleYearsV17:${this.baseUrl}:${this.version || 'current'}:${this.state.makeId}`;
    }

    // V18 FAST YEAR INDEX ------------------------------------------------------
    // Preferred R2 file:
    //   catalog/year-index/<make_id>.json
    // Recommended schema:
    //   {
    //     "schema": "svaico-vehicle-year-index-v1",
    //     "make_id": "123",
    //     "complete": true,
    //     "years": { "2007": ["model-id-1", "model-id-2"] }
    //   }
    //
    // This is a DERIVED navigation index only. It never replaces the authoritative
    // make/model/year/hp fitment files. If it is missing, V18 safely falls back to
    // embedded metadata, browser cache and finally model-index scanning.
    yearIndexRelativePath(makeId = this.state.makeId) {
      return `catalog/year-index/${encodeURIComponent(text(makeId))}.json`;
    }

    async fetchServerYearIndex(makeId) {
      if (!makeId) return null;
      const relative = this.yearIndexRelativePath(makeId);
      try {
        const url = new URL(`${this.baseUrl}/${relative}`);
        if (this.version) url.searchParams.set('v', this.version);
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          cache: 'force-cache'
        });
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`${response.status} ${relative}`);
        return await response.json();
      } catch (error) {
        // Optional accelerator: never break the picker just because the fast index is
        // absent/stale/unreachable. The normal fallback paths below remain authoritative.
        console.warn('SVAICO V18 year index unavailable; using fallback discovery.', error);
        return null;
      }
    }

    readYearModelsFromPayload(payload, validModelIds) {
      const map = new Map();
      if (!payload || typeof payload !== 'object') return { map, complete: false };

      const addId = (year, modelId) => {
        const numericYear = Number(year);
        const id = text(modelId);
        if (!Number.isInteger(numericYear) || numericYear < 1880 || numericYear > 2100) return;
        if (!id || (validModelIds?.size && !validModelIds.has(id))) return;
        this.addModelYearsToIndex(map, id, [numericYear]);
      };

      // Preferred compact shape: years -> [model ids]
      const yearsObject = payload.years || payload.year_models || payload.models_by_year;
      if (yearsObject && typeof yearsObject === 'object' && !Array.isArray(yearsObject)) {
        for (const [year, ids] of Object.entries(yearsObject)) {
          if (Array.isArray(ids)) ids.forEach((id) => addId(year, typeof id === 'object' ? (id.id ?? id.model_id) : id));
        }
      }

      // Forward-compatible alternative: models -> years.
      const modelsObject = payload.models;
      if (Array.isArray(modelsObject)) {
        for (const model of modelsObject) {
          const id = model?.id ?? model?.model_id;
          for (const year of this.modelYearsFromCatalogItem(model)) addId(year, id);
        }
      } else if (modelsObject && typeof modelsObject === 'object') {
        for (const [modelId, value] of Object.entries(modelsObject)) {
          if (Array.isArray(value)) value.forEach((year) => addId(year, modelId));
          else if (value && typeof value === 'object') {
            const years = this.modelYearsFromCatalogItem({ ...value, id: modelId });
            years.forEach((year) => addId(year, modelId));
          }
        }
      }

      const complete = payload.complete === true || payload.is_complete === true || text(payload.status).toLowerCase() === 'complete';
      return { map, complete };
    }

    readCachedYearModels(validModelIds) {
      try {
        const cached = JSON.parse(localStorage.getItem(this.yearIndexStorageKey()) || 'null');
        if (!cached || !cached.years || typeof cached.years !== 'object') return null;
        const map = new Map();
        for (const [year, ids] of Object.entries(cached.years)) {
          const filtered = Array.isArray(ids) ? ids.map(text).filter((id) => validModelIds.has(id)) : [];
          if (filtered.length) map.set(String(year), new Set(filtered));
        }
        return map.size ? map : null;
      } catch (_) { return null; }
    }

    persistYearModels(map) {
      try {
        const years = {};
        for (const [year, ids] of map) years[String(year)] = [...ids];
        localStorage.setItem(this.yearIndexStorageKey(), JSON.stringify({ saved_at: Date.now(), years }));
      } catch (_) {}
    }

    addModelYearsToIndex(index, modelId, years) {
      for (const year of years || []) {
        const numericYear = Number(year);
        if (!Number.isInteger(numericYear) || numericYear < 1880 || numericYear > 2100) continue;
        const key = String(numericYear);
        if (!index.has(key)) index.set(key, new Set());
        index.get(key).add(text(modelId));
      }
    }

    mergeYearModelIndex(target, source) {
      if (!(source instanceof Map)) return;
      for (const [year, ids] of source) {
        for (const id of ids || []) this.addModelYearsToIndex(target, id, [year]);
      }
    }

    async getModelData(modelId) {
      const key = text(modelId);
      if (this.modelDataCache.has(key)) return this.modelDataCache.get(key);
      const data = await this.fetchJson(`${this.state.makeId}/${encodeURIComponent(key)}/index.json`);
      this.modelDataCache.set(key, data);
      return data;
    }

    async buildYearModelIndex(serverPayload = null) {
      const models = Array.isArray(this.state.makeCatalog?.models) ? this.state.makeCatalog.models : [];
      const validModelIds = new Set(models.map((item) => text(item.id)));
      const index = new Map();

      // 1) Inline make-catalog metadata (zero extra requests when a future builder embeds it).
      for (const model of models) {
        const years = this.modelYearsFromCatalogItem(model);
        if (years.length) this.addModelYearsToIndex(index, model.id, years);
      }

      // 2) V18 server-side index. This is the permanent fast path: one tiny R2 file per make.
      const server = this.readYearModelsFromPayload(serverPayload, validModelIds);
      this.mergeYearModelIndex(index, server.map);

      // 3) Existing browser cache. This makes repeat visits instant even before the pipeline
      //    has been updated to publish the V18 server-side index.
      const cached = this.readCachedYearModels(validModelIds);
      if (cached) this.mergeYearModelIndex(index, cached);

      // If the pipeline marks the V18 index complete, do NOT inspect hundreds of model files.
      if (server.complete) {
        this.persistYearModels(index);
        return index;
      }

      const indexedIds = new Set([...index.values()].flatMap((ids) => [...ids]));
      const unresolved = models.filter((model) => !indexedIds.has(text(model.id)));

      // 4) Legacy fallback only. Older R2 output has years solely inside each model index.
      // V18 doubles the bounded pool from 12 -> 24 for a faster first visit, but this path
      // should disappear from normal customer traffic once the pipeline publishes year-index.
      if (unresolved.length) {
        this.setStatus(`Loading available years... 0/${unresolved.length.toLocaleString()} models checked.`);
        let completed = 0;
        await this.runPool(unresolved, 24, async (model) => {
          try {
            const data = await this.getModelData(model.id);
            this.addModelYearsToIndex(index, model.id, Object.keys(data?.routes || {}));
          } finally {
            completed += 1;
            if (completed === unresolved.length || completed % 20 === 0) {
              this.setStatus(`Loading available years... ${completed.toLocaleString()}/${unresolved.length.toLocaleString()} models checked.`);
            }
          }
        });
      }

      this.persistYearModels(index);
      return index;
    }

    async onMake() {
      this.selectionChanged();
      this.resetAfter('make');
      this.state.makeId = this.make.value;
      if (!this.state.makeId) return;
      try {
        this.setStatus('Loading available years...');

        // IMPORTANT V18 PERFORMANCE DETAIL:
        // Load the ordinary make catalogue and the optional tiny year index in PARALLEL.
        // With the pipeline-side year index present, no per-model discovery requests are needed.
        const [makeCatalog, serverYearIndex] = await Promise.all([
          this.fetchJson(`catalog/makes/${encodeURIComponent(this.state.makeId)}.json`),
          this.fetchServerYearIndex(this.state.makeId)
        ]);
        this.state.makeCatalog = makeCatalog;
        this.state.yearModels = await this.buildYearModelIndex(serverYearIndex);

        const years = [...this.state.yearModels.keys()].map(Number).filter(Number.isFinite).sort((a, b) => a - b);
        this.fill(this.year, years, 'Select year', (item) => item, (item) => item);
        this.setStatus(years.length
          ? `${years.length.toLocaleString()} years available. Select your vehicle year.`
          : 'No years are available for this make.');
      } catch (error) { this.fail(error, 'Could not load years for this make.'); }
    }

    modelYearRange(modelId) {
      const years = [];
      const id = text(modelId);
      for (const [year, ids] of this.state.yearModels || []) {
        if (!ids?.has(id)) continue;
        const numeric = Number(year);
        if (Number.isInteger(numeric)) years.push(numeric);
      }
      if (!years.length) return '';
      years.sort((a, b) => a - b);
      const first = years[0];
      const last = years[years.length - 1];
      return first === last ? `Year ${first}` : `Years ${first}–${last}`;
    }

    modelPresentation(item) {
      const raw = text(item?.label || item?.name).trim();
      let primary = raw;
      let detail = '';

      // Most current SVAICO model labels use "MODEL / GENERATION-OR-CHASSIS".
      // Keep the source label authoritative, but present the two parts separately so a
      // customer can scan by familiar model name first.
      const slash = raw.match(/^(.*?)\s*\/\s*(.+)$/);
      if (slash) {
        primary = slash[1].trim() || raw;
        const suffix = slash[2].trim();
        detail = /^[IVXLCDM]+$/i.test(suffix)
          ? `Generation ${suffix.toUpperCase()}`
          : `Variant / chassis: ${suffix}`;
      } else {
        // Also understand common labels such as "C-Class (W204)" without rewriting
        // ordinary model names that happen to contain punctuation.
        const trailingCode = raw.match(/^(.*?)\s+\(([A-Z0-9][A-Z0-9._-]{1,15})\)$/i);
        if (trailingCode) {
          primary = trailingCode[1].trim() || raw;
          detail = `Variant / chassis: ${trailingCode[2]}`;
        }
      }

      const range = this.modelYearRange(item?.id);
      const secondary = [detail, range].filter(Boolean).join(' · ');
      const display = secondary ? `${primary} — ${secondary}` : primary;
      return { raw, primary, secondary, display };
    }

    fillModels(items) {
      const options = items.map((item) => {
        const view = this.modelPresentation(item);
        return `<option value="${escapeHtml(item.id)}"
          data-r2-primary="${escapeHtml(view.primary)}"
          data-r2-secondary="${escapeHtml(view.secondary)}"
          data-r2-display="${escapeHtml(view.display)}">${escapeHtml(view.raw)}</option>`;
      });
      this.model.innerHTML = [`<option value="">Search model</option>`, ...options].join('');
      this.model.disabled = items.length === 0;
      this.searchableSelects.get(this.model)?.sync();
    }

    async onYear() {
      this.selectionChanged();
      this.resetAfter('year');
      this.state.year = this.year.value;
      if (!this.state.year) return;
      try {
        const ids = this.state.yearModels?.get(String(this.state.year)) || new Set();
        const models = (this.state.makeCatalog?.models || [])
          .filter((item) => ids.has(text(item.id)))
          .sort((a, b) => text(a.label || a.name).localeCompare(text(b.label || b.name), undefined, { numeric: true }));
        this.fillModels(models);
        this.setStatus(models.length
          ? `${models.length.toLocaleString()} models available for ${this.state.year}. Type the model name; generation/chassis and available years are shown in the list.`
          : `No models are available for ${this.state.year}.`);
      } catch (error) { this.fail(error, 'Could not load models for this year.'); }
    }

    async onModel() {
      this.selectionChanged();
      this.resetAfter('model');
      this.state.modelId = this.model.value;
      if (!this.state.modelId) return;
      try {
        this.setStatus('Loading engine powers...');
        this.state.modelData = await this.getModelData(this.state.modelId);
        const powers = Object.keys(this.state.modelData?.routes?.[String(this.state.year)] || {})
          .map(Number).filter(Number.isFinite).sort((a, b) => a - b);
        this.fill(this.power, powers, 'Select engine power', (item) => item, (item) => `${item} HP`);
        this.setStatus(powers.length ? 'Select engine power.' : 'No engine power options are available for this model and year.');
      } catch (error) { this.fail(error, 'Could not load engine powers.'); }
    }

    onPower() {
      this.selectionChanged();
      this.state.hp = this.power.value;
      this.setStatus(this.state.hp ? 'Press Find parts to filter the catalogue.' : 'Select engine power.');
    }

    async applyVehicleFilter({ scroll = false, remember = true } = {}) {
      if (!this.state.makeId || !this.state.modelId || !this.state.year || !this.state.hp) {
        this.setStatus('Select make, year, model and engine power first.', true);
        return false;
      }
      try {
        const setId = this.state.modelData?.routes?.[String(this.state.year)]?.[String(this.state.hp)];
        if (!setId) throw new Error('No product set exists for this vehicle.');
        this.setStatus('Filtering the catalogue for your vehicle…');
        this.state.leaf = await this.loadProductSet(setId);
        this.state.category = null;
        this.state.subgroup = null;
        this.productMode = 'fitment';
        if (remember) this.rememberVehicle();
        this.closeDetails({ replaceHistory: true });
        this.renderCategories();
        this.updateCatalogSummary();
        const selected = this.selectedVehicleLabel();
        this.setStatus(`${Number(this.state.leaf?.total || 0).toLocaleString()} compatible products found for ${selected}.`);
        if (scroll) this.categories?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      } catch (error) {
        this.fail(error, 'Could not filter parts for this vehicle.');
        return false;
      }
    }

    async restoreSavedVehicle() {
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem('svaicoVehicle') || 'null'); }
      catch (_) { saved = null; }
      if (!saved) return false;

      const choose = (select, preferredValue, preferredLabel) => {
        const exact = Array.from(select.options).find((option) => text(option.value) === text(preferredValue));
        const byLabel = Array.from(select.options).find((option) => normalizeSearch(option.textContent) === normalizeSearch(preferredLabel));
        const option = exact || byLabel;
        if (!option) return false;
        select.value = option.value;
        this.searchableSelects.get(select)?.sync();
        return true;
      };

      this.restoring = true;
      try {
        if (!choose(this.make, saved.makeId, saved.make)) return false;
        await this.onMake();
        if (!choose(this.year, saved.year, saved.year)) return false;
        await this.onYear();
        if (!choose(this.model, saved.modelId, saved.model)) return false;
        await this.onModel();
        const hp = text(saved.hp || saved.power).replace(/\s*HP\s*$/i, '');
        if (!choose(this.power, hp, `${hp} HP`)) return false;
        this.state.hp = this.power.value;
        this.restoring = false;
        return await this.applyVehicleFilter({ scroll: false, remember: false });
      } catch (error) {
        console.warn('Could not restore saved vehicle', error);
        return false;
      } finally {
        this.restoring = false;
      }
    }

    selectedVehicleLabel() {
      return [
        this.make?.options?.[this.make.selectedIndex]?.text || '',
        this.state.year,
        this.model?.options?.[this.model.selectedIndex]?.text || '',
        this.state.hp ? `${this.state.hp} HP` : ''
      ].filter(Boolean).join(' • ');
    }

    updateCatalogSummary() {
      if (!this.summary) return;
      if (this.state.leaf) {
        const total = Number(this.state.leaf?.total || 0);
        this.summary.textContent = `${total.toLocaleString()} compatible products found.`;
        if (this.clearFilter) this.clearFilter.hidden = false;
        return;
      }

      const total = Number(this.catalogTotal || 0);
      if (this.catalogCountsReady && total >= 0) {
        this.summary.textContent = `${total.toLocaleString()} products available.`;
      } else if (this.catalogTotalExact && total > 0) {
        this.summary.textContent = `${total.toLocaleString()} products available.`;
      } else if (total >= SHOPIFY_COUNT_CAP_SIGNAL) {
        this.summary.textContent = `${total.toLocaleString()}+ products available.`;
      } else {
        this.summary.textContent = 'Browse the full catalogue.';
      }
      if (this.clearFilter) this.clearFilter.hidden = true;
    }

    catalogCountValue(value) {
      if (Number.isFinite(Number(value))) return Number(value);
      if (value && typeof value === 'object') {
        const candidate = value.count ?? value.total ?? value.products;
        if (Number.isFinite(Number(candidate))) return Number(candidate);
      }
      return null;
    }

    matchMasterCategory(name) {
      const key = normalize(name);
      return this.master.find((category) => category.key === key) || null;
    }

    matchMasterSubgroup(name) {
      const key = normalize(name);
      for (const category of this.master) {
        const subgroup = category.subgroups.find((item) => item.key === key);
        if (subgroup) return subgroup;
      }
      return null;
    }

    applyCatalogCountsPayload(payload, { persist = false } = {}) {
      if (!payload || typeof payload !== 'object') return false;
      this.syncMasterCatalogFromCounts(payload);

      let applied = 0;
      const categories = payload.categories && typeof payload.categories === 'object' ? payload.categories : {};
      const topSubgroups = payload.subgroups && typeof payload.subgroups === 'object' ? payload.subgroups : {};

      for (const [name, value] of Object.entries(categories)) {
        const master = this.matchMasterCategory(name);
        if (!master) continue;
        const count = this.catalogCountValue(value);
        if (Number.isFinite(count) && count >= 0) {
          this.setCategoryCount(master.name, count);
          this.categoryCountExact.set(master.name, true);
          applied += 1;
        }
        const nested = value && typeof value === 'object' && value.subgroups && typeof value.subgroups === 'object'
          ? value.subgroups : {};
        for (const [subName, subValue] of Object.entries(nested)) {
          const subgroup = master.subgroups.find((item) => item.key === normalize(subName));
          const subCount = this.catalogCountValue(subValue);
          if (Number.isFinite(subCount) && subCount >= 0) {
            // Cache the exact category+subgroup count even when a zero-count
            // subgroup is intentionally omitted from the visible master list.
            this.setSubgroupCount(master.name, subName, subCount);
            if (subgroup) applied += 1;
          }
        }
      }

      // Backward-compatible top-level subgroup counts are only safe when the
      // subgroup name is unique across all current categories.
      for (const [name, value] of Object.entries(topSubgroups)) {
        const key = normalize(name);
        const matches = [];
        for (const category of this.master) {
          const subgroup = category.subgroups.find((item) => item.key === key);
          if (subgroup) matches.push({ category, subgroup });
        }
        const count = this.catalogCountValue(value);
        if (matches.length === 1 && Number.isFinite(count) && count >= 0) {
          this.setSubgroupCount(matches[0].category.name, matches[0].subgroup.name, count);
          applied += 1;
        }
      }

      const total = this.catalogCountValue(payload.total ?? payload.product_count ?? payload.products);
      if (Number.isFinite(total) && total >= 0) {
        this.catalogTotal = total;
        this.catalogTotalExact = true;
        applied += 1;
      } else {
        const categoryCounts = this.master.map((category) => this.getCategoryCount(category.name));
        if (categoryCounts.every(Number.isFinite)) {
          this.catalogTotal = categoryCounts.reduce((sum, value) => sum + Number(value || 0), 0);
          this.catalogTotalExact = true;
          applied += 1;
        }
      }

      if (!applied) return false;
      this.catalogCountsReady = true;
      this.catalogCountsGeneratedAt = text(payload.generated_at || payload.updated_at || '');
      if (persist) {
        try {
          localStorage.setItem(this.catalogCountsStorageKey, JSON.stringify({ saved_at: Date.now(), payload }));
        } catch (_) {}
      }
      return true;
    }

    applyCachedCatalogCounts() {
      if (!this.catalogCountsUrl) return false;
      try {
        const cached = JSON.parse(localStorage.getItem(this.catalogCountsStorageKey) || 'null');
        return this.applyCatalogCountsPayload(cached?.payload || cached, { persist: false });
      } catch (_) {
        return false;
      }
    }

    refreshCatalogCountUi() {
      if (this.state.leaf) return;
      this.renderCategories();
      if (this.state.category?.master && this.subgroups && !this.subgroups.hidden) {
        const categoryName = this.state.category.master.name;
        for (const button of Array.from(this.subgroupGrid?.querySelectorAll('[data-r2-subgroup]') || [])) {
          const key = button.dataset.r2Subgroup || '';
          const subgroup = this.state.category.master.subgroups.find((item) => item.key === key);
          if (!subgroup) continue;
          const count = this.getSubgroupCount(categoryName, subgroup.name);
          if (Number.isFinite(count) && count <= 0) {
            button.remove();
            continue;
          }
          const node = button.querySelector(`[data-r2-subgroup-count="${CSS.escape(subgroup.key)}"]`);
          if (node && Number.isFinite(count)) node.textContent = `${Number(count).toLocaleString()} products`;
        }
      }
      this.updateCatalogSummary();
    }

    async loadCatalogCounts() {
      if (!this.catalogCountsUrl) {
        this.updateCatalogSummary();
        return;
      }
      try {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = controller ? setTimeout(() => controller.abort(), 5000) : null;
        const response = await fetch(this.catalogCountsUrl, {
          headers: { Accept: 'application/json' },
          cache: 'no-cache',
          signal: controller?.signal
        });
        if (timer) clearTimeout(timer);
        if (!response.ok) throw new Error(`${response.status} catalogue counts`);
        const payload = await response.json();
        if (!this.applyCatalogCountsPayload(payload, { persist: true })) {
          throw new Error('Catalogue counts JSON did not contain usable counts');
        }
        this.refreshCatalogCountUi();
      } catch (error) {
        console.warn('Could not refresh catalogue counts; using cached/fallback values.', error);
        this.updateCatalogSummary();
      }
    }

    materializeSet(definition, products) {
      return {
        total: Number(definition?.total || 0),
        categories: (definition?.categories || []).map((category) => ({
          ...category,
          subgroups: (category.subgroups || []).map((subgroup) => ({
            ...subgroup,
            products: Array.isArray(subgroup.products)
              ? subgroup.products.map((value) => Number.isInteger(value) ? products[value] : value).filter(Boolean)
              : subgroup.products
          }))
        }))
      };
    }

    async loadProductSet(setId) {
      const model = this.state.modelData || {};
      if (model.storage_mode === 'model-inline-deduplicated') {
        const definition = model.sets?.[setId];
        if (!definition) throw new Error(`Missing product set ${setId}`);
        return this.materializeSet(definition, model.products || []);
      }
      const storage = model.set_storage?.[setId];
      if (!storage?.file) throw new Error(`Missing storage for product set ${setId}`);
      const relative = `${this.state.makeId}/${this.state.modelId}/${storage.file}`;
      const payload = await this.fetchJson(relative);
      if (storage.mode === 'shard') {
        const definition = payload.sets?.[setId];
        if (!definition) throw new Error(`Missing product set ${setId} in shard`);
        return this.materializeSet(definition, payload.products || []);
      }
      if (storage.mode === 'external') {
        return { total: Number(payload.total || 0), categories: payload.categories || [] };
      }
      throw new Error(`Unknown product-set storage mode: ${storage.mode}`);
    }

    rememberVehicle() {
      const selected = {
        vehicleId: `${this.state.makeId}-${this.state.modelId}-${this.state.year}-${this.state.hp}`,
        make: this.make.options[this.make.selectedIndex]?.text || '',
        model: this.model.options[this.model.selectedIndex]?.text || '',
        year: this.state.year,
        power: `${this.state.hp} HP`,
        makeId: this.state.makeId,
        modelId: this.state.modelId,
        hp: this.state.hp
      };
      try {
        localStorage.setItem('svaicoVehicle', JSON.stringify(selected));
        const history = JSON.parse(localStorage.getItem('svaicoVehicleHistory') || '[]');
        const next = [selected, ...history.filter((item) => item.vehicleId !== selected.vehicleId)].slice(0, 5);
        localStorage.setItem('svaicoVehicleHistory', JSON.stringify(next));
      } catch (_) {}
      document.dispatchEvent(new CustomEvent('svaico:vehicle-selected', { detail: selected }));
    }

    fitmentCategory(masterCategory) {
      const key = masterCategory.key;
      return (this.state.leaf?.categories || []).find((category) => normalize(category.name) === key) || null;
    }

    fitmentSubgroup(r2Category, masterSubgroup) {
      if (!r2Category) return null;
      return (r2Category.subgroups || []).find((subgroup) => normalize(subgroup.name) === masterSubgroup.key) || null;
    }

    renderCategories() {
      if (!this.categoryGrid) return;
      this.categories.hidden = false;
      this.categoryGrid.innerHTML = this.master.map((category) => {
        const visual = categoryVisual(category.name);
        const r2Category = this.state.leaf ? this.fitmentCategory(category) : null;
        const count = this.state.leaf
          ? Number(r2Category?.total || 0)
          : this.getCategoryCount(category.name);
        const countText = this.state.leaf
          ? `${Number(count || 0).toLocaleString()} products`
          : Number.isFinite(count)
            ? `${Number(count).toLocaleString()} products`
            : 'View products';
        const disabled = Boolean(this.state.leaf && !r2Category);
        if (disabled) {
          return `<button type="button" class="vehicle-category-card is-unavailable"
              data-r2-category="${escapeHtml(category.key)}" disabled aria-disabled="true">
            <div class="vehicle-category-icon vehicle-category-icon--${escapeHtml(visual.id)}">
              <svg viewBox="0 0 64 64" aria-hidden="true">${visual.svg}</svg>
            </div>
            <div class="vehicle-category-content">
              <h2>${escapeHtml(category.name)}</h2>
              <p>${escapeHtml(visual.description)} · <span data-r2-category-count="${escapeHtml(category.key)}">${escapeHtml(countText)}</span></p>
            </div>
            <span class="vehicle-category-arrow">↗</span>
          </button>`;
        }
        const href = this.crawlCollectionUrl(category.name);
        return `<a class="vehicle-category-card" href="${escapeHtml(href)}"
            data-r2-category="${escapeHtml(category.key)}" aria-label="Browse ${escapeHtml(category.name)} parts">
          <div class="vehicle-category-icon vehicle-category-icon--${escapeHtml(visual.id)}">
            <svg viewBox="0 0 64 64" aria-hidden="true">${visual.svg}</svg>
          </div>
          <div class="vehicle-category-content">
            <h2>${escapeHtml(category.name)}</h2>
            <p>${escapeHtml(visual.description)} · <span data-r2-category-count="${escapeHtml(category.key)}">${escapeHtml(countText)}</span></p>
          </div>
          <span class="vehicle-category-arrow">↗</span>
        </a>`;
      }).join('');
      this.updateCatalogSummary();
    }

    async hydrateCategoryCounts() {
      // V11 deliberately avoids per-category storefront count requests.
      // One precomputed R2 JSON object supplies all homepage counts.
      this.refreshCatalogCountUi();
    }

    selectCategory(key, options = {}) {
      const category = this.master.find((item) => item.key === key);
      if (!category) return;
      const r2Category = this.state.leaf ? this.fitmentCategory(category) : null;
      if (this.state.leaf && !r2Category) return;
      this.state.category = { master: category, r2: r2Category };
      this.state.subgroup = null;
      if (this.subgroupHeading) this.subgroupHeading.textContent = category.name;

      const rows = this.state.leaf
        ? category.subgroups.map((subgroup) => ({ master: subgroup, r2: this.fitmentSubgroup(r2Category, subgroup) })).filter((item) => item.r2)
        : category.subgroups
          .filter((subgroup) => {
            const count = this.getSubgroupCount(category.name, subgroup.name);
            return !Number.isFinite(count) || count > 0;
          })
          .map((subgroup) => ({ master: subgroup, r2: null }));

      this.subgroupGrid.innerHTML = rows.map(({ master, r2 }) => {
        const total = this.state.leaf ? Number(r2?.total ?? (Array.isArray(r2?.products) ? r2.products.length : 0)) : this.getSubgroupCount(category.name, master.name);
        const countText = this.state.leaf
          ? `${Number(total || 0).toLocaleString()} products`
          : Number.isFinite(total) ? `${Number(total).toLocaleString()} products` : 'View products';
        const visual = categoryVisual(category.name);
        const href = this.mobileSubgroupHref(category.name, master, r2);
        return `<a class="subgroup-card" href="${escapeHtml(href)}" data-r2-subgroup="${escapeHtml(master.key)}"
            aria-label="Browse ${escapeHtml(master.name)} in ${escapeHtml(category.name)}">
          <div class="subgroup-icon"><svg viewBox="0 0 64 64" aria-hidden="true">${visual.svg}</svg></div>
          <div><h2>${escapeHtml(master.name)}</h2><p>${master.description ? `${escapeHtml(master.description)} · ` : ''}<span data-r2-subgroup-count="${escapeHtml(master.key)}">${escapeHtml(countText)}</span></p></div>
          <span>↗</span>
        </a>`;
      }).join('') || '<div class="subgroup-empty">No compatible part types are available in this category.</div>';

      this.subgroups.hidden = false;
      this.results.hidden = true;
      this.pagination.innerHTML = '';
      if (options.history !== false) this.pushCatalogHistory('subgroups');
      if (options.scroll !== false) this.scrollToPanelStart(this.subgroups, options.scrollBehavior || 'smooth');
      if (!this.state.leaf) this.hydrateSubgroupCounts(rows.map((item) => item.master));
    }

    async hydrateSubgroupCounts(subgroups) {
      // Counts are already present in catalog-counts.json when available.
      for (const subgroup of subgroups || []) {
        const count = this.getSubgroupCount(this.state.category.master.name, subgroup.name);
        const node = this.subgroupGrid?.querySelector(`[data-r2-subgroup-count="${CSS.escape(subgroup.key)}"]`);
        if (node && Number.isFinite(count)) node.textContent = `${Number(count).toLocaleString()} products`;
      }
    }

    selectSubgroup(key, options = {}) {
      const category = this.state.category;
      if (!category?.master) return;
      const master = category.master.subgroups.find((item) => item.key === key);
      if (!master) return;
      const r2 = this.state.leaf ? this.fitmentSubgroup(category.r2, master) : null;
      if (this.state.leaf && !r2) return;
      if (!this.state.leaf) {
        const count = this.getSubgroupCount(category.master.name, master.name);
        if (Number.isFinite(count) && count <= 0) return;
      }
      this.state.subgroup = { master, r2 };
      const page = Math.max(1, Number(options.page || 1));
      if (options.history !== false) this.pushCatalogHistory('results', page);
      return this.loadProducts(page, { historyRestore: options.history === false });
    }

    async loadProducts(page, options = {}) {
      if (!this.state.subgroup) return;
      if (this.state.leaf) return this.loadFitmentProducts(page, options);
      return this.loadShopifyProductsPage(page, options);
    }

    async loadShopifyProductsPage(page, options = {}) {
      const category = this.state.category?.master;
      const subgroup = this.state.subgroup?.master;
      if (!category || !subgroup) return;
      const safePage = Math.max(1, Number(page || 1));
      const isPagination = options.pagination === true;
      const expectedTotal = this.getSubgroupCount(category.name, subgroup.name);

      // A zero count from catalog-counts.json is authoritative. Do not hit a
      // Shopify tag route: an unknown/obsolete tag can fall back to /all and
      // falsely report ~25k products plus hundreds of pages.
      if (Number.isFinite(expectedTotal) && Number(expectedTotal) <= 0) {
        this.productMode = 'catalog';
        this.showResults();
        this.resultEyebrow.textContent = 'Catalogue';
        this.resultTitle.textContent = subgroup.name;
        this.resultStatus.textContent = `0 products available in ${subgroup.name}.`;
        this.resultGrid.innerHTML = '<div class="r2-fitment-empty">No products are available in this part type.</div>';
        this.pagination.innerHTML = '';
        return;
      }

      try {
        this.productMode = 'catalog';
        this.showResults({ scroll: false });
        this.resultEyebrow.textContent = 'Catalogue';
        this.resultTitle.textContent = subgroup.name;
        this.resultStatus.textContent = isPagination
          ? `Loading page ${safePage}…`
          : 'Showing all active products in this part type.';
        // V27 PAGINATION UX: keep the current product grid in place while a
        // new page is loading. Collapsing 50 cards into one loading row before
        // scrolling caused the browser to clamp the scroll position and jump
        // much farther up the homepage.
        if (!isPagination) {
          this.resultGrid.innerHTML = '<div class="r2-fitment-loading">Loading products…</div>';
          this.scrollToResultStart();
        } else {
          this.results?.setAttribute('aria-busy', 'true');
        }

        // A subgroup name can exist under more than one category. Shopify's
        // tag route must therefore use the intersection of BOTH current tags.
        const url = this.collectionTagUrl(category.name, subgroup.name, 'svaico-inline', safePage);
        let payload = null;
        let grid = null;
        let lastError = null;

        // Shopify can occasionally return HTTP 200 with a transient Liquid
        // "internal" error inside the rendered fragment. Never expose that
        // raw error to customers: detect it and retry with cache-busting.
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const retryUrl = new URL(url, window.location.origin);
            if (attempt > 0) retryUrl.searchParams.set('_svaico_retry', `${Date.now()}-${attempt}`);
            const response = await fetch(retryUrl.toString(), {
              headers: { Accept: 'text/html' },
              credentials: 'same-origin',
              cache: attempt > 0 ? 'no-store' : 'default'
            });
            if (!response.ok) throw new Error(`${response.status} Catalogue`);
            const html = await response.text();
            if (/Liquid error\s*\(/i.test(html) || /Liquid error:/i.test(html)) {
              throw new Error('Shopify returned a transient Liquid rendering error.');
            }
            const doc = new DOMParser().parseFromString(html, 'text/html');
            payload = doc.querySelector('[data-svaico-inline-catalog]');
            grid = doc.querySelector('[data-svaico-inline-grid]');
            if (!payload || !grid) throw new Error('Inline catalogue template was not returned.');
            if (/Liquid error/i.test(grid.textContent || '')) {
              throw new Error('Shopify returned a transient Liquid rendering error.');
            }
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
          }
        }
        if (lastError || !payload || !grid) throw lastError || new Error('Inline catalogue could not be rendered.');

        // Historical add-only taxonomy tags can temporarily leave products in
        // an old Shopify tag collection. The inline card exposes the CURRENT
        // metafield values, so remove stale matches before customers see them.
        const expectedCategory = normalize(category.name);
        const expectedSubgroup = normalize(subgroup.name);
        let staleCardsRemoved = 0;
        for (const card of Array.from(grid.children)) {
          const currentCategory = normalize(card.dataset.svaicoMainCategory || '');
          const currentSubgroup = normalize(card.dataset.svaicoSubgroup || '');
          if (currentCategory !== expectedCategory || currentSubgroup !== expectedSubgroup) {
            card.remove();
            staleCardsRemoved += 1;
          }
        }

        this.resultGrid.innerHTML = grid.innerHTML || '<div class="r2-fitment-empty">No products are available on this page.</div>';
        const shopifyTagTotal = Number(payload.dataset.total || 0);
        const displayTotal = Number.isFinite(expectedTotal) ? Number(expectedTotal) : shopifyTagTotal;
        const pages = Number(payload.dataset.pages || 1);
        const current = Number(payload.dataset.page || safePage);

        if (Number.isFinite(expectedTotal) && shopifyTagTotal !== Number(expectedTotal)) {
          console.warn('SVAICO taxonomy tag mismatch', {
            category: category.name,
            subgroup: subgroup.name,
            expectedTotal: Number(expectedTotal),
            shopifyTagTotal,
            staleCardsRemoved
          });
        }

        this.resultStatus.textContent = `${displayTotal.toLocaleString()} products available in ${subgroup.name}.`;
        this.renderPages(current, pages);
        this.replaceCatalogHistoryPage(current);
        this.results?.removeAttribute('aria-busy');
        if (isPagination) requestAnimationFrame(() => this.scrollToResultStart());
      } catch (error) {
        console.error(error);
        this.results?.removeAttribute('aria-busy');
        if (isPagination) {
          // Keep the page the customer was already looking at if the next
          // page fails to load. This avoids replacing useful results with an
          // error card because of a transient Shopify response.
          this.resultStatus.textContent = 'The next page could not be loaded right now. Please try again.';
          return;
        }
        this.resultGrid.innerHTML = '<div class="r2-fitment-empty">Products could not be loaded right now.<br><button type="button" class="button button--secondary" data-svaico-inline-retry>Try again</button></div>';
        this.resultStatus.textContent = 'Temporary catalogue loading issue.';
        this.resultGrid.querySelector('[data-svaico-inline-retry]')?.addEventListener('click', () => this.loadShopifyProductsPage(safePage), { once: true });
      }
    }

    async loadFitmentProducts(page, options = {}) {
      const subgroup = this.state.subgroup?.r2;
      if (!subgroup) return;
      const embedded = Array.isArray(subgroup.products) ? subgroup.products : null;
      const sharded = subgroup.storage_mode === 'page-shards' || subgroup.sharded === true || embedded === null;
      const pages = Number(subgroup.pages || (embedded ? Math.max(1, Math.ceil(embedded.length / Number(subgroup.page_size || 50))) : 1));
      const safePage = Math.max(1, Math.min(Number(page || 1), pages));
      const isPagination = options.pagination === true;
      let handles = embedded || [];
      try {
        this.productMode = 'fitment';
        this.showResults({ scroll: false });
        this.resultEyebrow.textContent = 'Compatible parts';
        this.resultTitle.textContent = this.state.subgroup.master.name;
        this.resultStatus.textContent = isPagination
          ? `Loading page ${safePage}…`
          : `Filtered for ${this.selectedVehicleLabel()}.`;
        if (!isPagination) {
          this.resultGrid.innerHTML = '<div class="r2-fitment-loading">Loading compatible products…</div>';
          this.scrollToResultStart();
        } else {
          this.results?.setAttribute('aria-busy', 'true');
        }
        if (sharded) {
          const shard = (subgroup.shards || []).find((item) => safePage >= Number(item.first_page) && safePage <= Number(item.last_page));
          if (!shard?.file) throw new Error(`Missing page shard for page ${safePage}`);
          const setId = this.state.modelData?.routes?.[String(this.state.year)]?.[String(this.state.hp)];
          const storage = this.state.modelData?.set_storage?.[setId];
          const base = storage?.file ? storage.file.replace(/\/index\.json$/, '') : '';
          const relative = `${this.state.makeId}/${this.state.modelId}/${base}/${shard.file}`.replace(/\/+/g, '/');
          const payload = await this.fetchJson(relative);
          handles = payload.pages?.[String(safePage)] || [];
        } else {
          const pageSize = Number(subgroup.page_size || 50);
          const start = (safePage - 1) * pageSize;
          handles = embedded.slice(start, start + pageSize);
        }
        const products = await this.fetchShopifyProducts(handles);
        this.renderFitmentProducts(products, handles);
        this.renderPages(safePage, pages);
        this.replaceCatalogHistoryPage(safePage);
        this.resultStatus.textContent = `${Number(subgroup.total || handles.length).toLocaleString()} compatible products for ${this.selectedVehicleLabel()}.`;
        this.results?.removeAttribute('aria-busy');
        if (isPagination) requestAnimationFrame(() => this.scrollToResultStart());
      } catch (error) {
        console.error(error);
        this.results?.removeAttribute('aria-busy');
        if (isPagination) {
          this.resultStatus.textContent = 'The next page could not be loaded right now. Please try again.';
          return;
        }
        this.resultGrid.innerHTML = '<div class="r2-fitment-empty">Compatible products could not be loaded.</div>';
        this.resultStatus.textContent = 'Could not load compatible products.';
      }
    }

    async fetchShopifyProducts(handles) {
      const results = new Array(handles.length).fill(null);
      let cursor = 0;
      const worker = async () => {
        while (cursor < handles.length) {
          const index = cursor++;
          const handle = handles[index];
          try {
            const rootPath = String(window.Shopify?.routes?.root || '/');
            const url = `${rootPath}products/${encodeURIComponent(handle)}?view=svaico-card-json`.replace(/\/+/g, '/');
            const response = await fetch(url, { headers: { Accept: 'application/json' } });
            if (response.ok) results[index] = await response.json();
          } catch (_) {}
        }
      };
      await Promise.all(Array.from({ length: Math.min(8, handles.length) }, worker));
      return results;
    }


    extendedLeadTimeHtml(product) {
      if (!product?.svaico_extended_lead_time) return '';
      const tooltip = 'This is the maximum estimated dispatch time. In many cases, the product may be dispatched sooner.';
      return `<div class="product-dispatch-lead-time product-dispatch-lead-time--extended product-dispatch-lead-time--card" data-svaico-dispatch-lead-time data-svaico-lead-time-class="extended">
        <div class="product-dispatch-lead-time__heading">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8"></circle><path d="M12 8v5l3 2"></path></svg>
          <strong>Extended lead time</strong>
          <span class="product-dispatch-lead-time__info" tabindex="0" aria-label="${tooltip}" title="${tooltip}">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v6"></path><path d="M12 7.5h.01"></path></svg>
            <span class="product-dispatch-lead-time__tooltip" role="tooltip">${tooltip}</span>
          </span>
        </div>
        <div class="product-dispatch-lead-time__estimate">Estimated dispatch from SVAICO: <strong>up to 30 business days</strong></div>
      </div>`;
    }

    renderFitmentProducts(products, handles) {
      this.resultGrid.innerHTML = handles.map((handle, index) => {
        const product = products[index];
        if (!product) return `<a class="r2-product-card r2-product-card--missing" href="/products/${encodeURIComponent(handle)}"><strong>${escapeHtml(handle)}</strong><span>Open product</span></a>`;
        const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0];
        const image = product.featured_image || product.images?.[0] || placeholderFor(this.state?.subgroup?.master?.name || '', product.title);
        const price = this.money(variant?.price ?? product.price ?? 0);
        return `<article class="r2-product-card">
          <a href="${escapeHtml(product.url || `/products/${handle}`)}" class="r2-product-card__image">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy"${(!product.featured_image && !(product.images?.[0])) ? ' class="svaico-placeholder-image"' : ''}>` : ''}</a>
          <div class="r2-product-card__body">
            <a href="${escapeHtml(product.url || `/products/${handle}`)}"><strong>${escapeHtml(product.title)}</strong></a>
            <span class="r2-product-card__price">${escapeHtml(price)}</span>
          </div>
          ${this.extendedLeadTimeHtml(product)}
        </article>`;
      }).join('');
    }

    money(cents) {
      const amount = Number(cents || 0) / 100;
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'NOK' }).format(amount); }
      catch (_) { return `${amount.toFixed(2)} NOK`; }
    }

    renderPages(current, pages) {
      if (pages <= 1) { this.pagination.innerHTML = ''; return; }
      const buttons = [];
      buttons.push(`<button type="button" data-r2-page="${current - 1}" ${current === 1 ? 'disabled' : ''}>Previous</button>`);
      for (let page = 1; page <= pages; page++) {
        if (pages > 9 && page > 3 && page < pages - 2 && Math.abs(page - current) > 1) {
          if (buttons[buttons.length - 1] !== '<span>…</span>') buttons.push('<span>…</span>');
          continue;
        }
        buttons.push(`<button type="button" data-r2-page="${page}" ${page === current ? 'class="is-current" aria-current="page"' : ''}>${page}</button>`);
      }
      buttons.push(`<button type="button" data-r2-page="${current + 1}" ${current === pages ? 'disabled' : ''}>Next</button>`);
      this.pagination.innerHTML = buttons.join('');
    }

    scrollToResultStart() {
      const target = this.results?.querySelector('.r2-home-results-head') || this.resultTitle || this.results;
      if (!target) return;
      const headerHeight = document.querySelector('.site-header')?.getBoundingClientRect().height || 0;
      const announcementHeight = document.querySelector('.announcement')?.getBoundingClientRect().height || 0;
      const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - headerHeight - announcementHeight - 16);
      window.scrollTo({ top, behavior: 'smooth' });
    }

    showResults({ scroll = true } = {}) {
      this.results.hidden = false;
      if (scroll) this.scrollToResultStart();
    }

    catalogHistoryValue() {
      const state = window.history.state;
      return state && typeof state === 'object' ? state[this.catalogHistoryField] : null;
    }

    catalogHistoryPayload(view, page = 1) {
      return {
        version: 2,
        view,
        category: this.state.category?.master?.key || '',
        subgroup: this.state.subgroup?.master?.key || '',
        page: Math.max(1, Number(page || 1)),
        scrollY: Math.max(0, Math.round(Number(window.scrollY || 0)))
      };
    }

    writeCatalogHistory(view, { replace = false, page = 1 } = {}) {
      try {
        const previous = window.history.state && typeof window.history.state === 'object'
          ? window.history.state : {};
        const next = { ...previous, [this.catalogHistoryField]: this.catalogHistoryPayload(view, page) };
        window.history[replace ? 'replaceState' : 'pushState'](next, '', window.location.href);
      } catch (_) {}
    }

    ensureCatalogHistoryRoot() {
      if (this.catalogHistoryValue()) return;
      this.writeCatalogHistory('categories', { replace: true });
    }

    pushCatalogHistory(view, page = 1) {
      const current = this.catalogHistoryValue();
      const next = this.catalogHistoryPayload(view, page);
      if (current && current.view === next.view && current.category === next.category && current.subgroup === next.subgroup) {
        this.writeCatalogHistory(view, { replace: true, page });
        return;
      }
      this.writeCatalogHistory(view, { page });
    }

    replaceCatalogHistoryPage(page) {
      const current = this.catalogHistoryValue();
      if (!current || current.view !== 'results') return;
      this.writeCatalogHistory('results', { replace: true, page });
    }

    rememberCatalogPosition() {
      const current = this.catalogHistoryValue();
      if (!current || !current.view) return;
      try {
        const previous = window.history.state && typeof window.history.state === 'object'
          ? window.history.state : {};
        const nextNav = {
          ...current,
          version: 2,
          scrollY: Math.max(0, Math.round(Number(window.scrollY || 0)))
        };
        window.history.replaceState({ ...previous, [this.catalogHistoryField]: nextNav }, '', window.location.href);
      } catch (_) {}
    }

    replaceCatalogHistorySnapshot(nav) {
      if (!nav || typeof nav !== 'object' || !nav.view) return;
      try {
        const previous = window.history.state && typeof window.history.state === 'object'
          ? window.history.state : {};
        const nextNav = {
          ...nav,
          version: 2,
          page: Math.max(1, Number(nav.page || 1)),
          scrollY: Number.isFinite(Number(nav.scrollY)) ? Math.max(0, Math.round(Number(nav.scrollY))) : 0
        };
        window.history.replaceState({ ...previous, [this.catalogHistoryField]: nextNav }, '', window.location.href);
      } catch (_) {}
    }

    restoreSavedScroll(nav, fallbackTarget = null) {
      const savedY = Number(nav?.scrollY);
      if (Number.isFinite(savedY) && savedY > 0) {
        // Two frames let the freshly injected product grid establish its height
        // before restoring the old scroll position. This is important when Back
        // causes a real reload instead of a BFCache restore.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          window.scrollTo({ top: Math.max(0, savedY), behavior: 'auto' });
        }));
        return;
      }
      if (fallbackTarget) this.scrollToPanelStart(fallbackTarget, 'auto');
    }

    async restoreCatalogHistorySnapshot(nav, { onLoad = false } = {}) {
      if (!nav || typeof nav !== 'object' || !nav.view) return false;

      if (nav.view === 'categories') {
        this.closeDetails({ history: false, scroll: false });
        this.replaceCatalogHistorySnapshot(nav);
        if (onLoad) this.restoreSavedScroll(nav, this.categories);
        return true;
      }

      if (nav.view === 'subgroups' && nav.category) {
        this.selectCategory(nav.category, { history: false, scroll: false });
        if (!this.state.category) return false;
        this.replaceCatalogHistorySnapshot(nav);
        if (onLoad) this.restoreSavedScroll(nav, this.subgroups);
        else this.scrollToPanelStart(this.subgroups, 'auto');
        return true;
      }

      if (nav.view === 'results' && nav.category && nav.subgroup) {
        this.selectCategory(nav.category, { history: false, scroll: false });
        if (!this.state.category) return false;
        await this.selectSubgroup(nav.subgroup, { history: false, page: nav.page || 1 });
        if (!this.state.subgroup) return false;
        // loadProducts() can replace the history entry while restoring the page;
        // put the captured return state back afterwards, including exact scrollY.
        this.replaceCatalogHistorySnapshot(nav);
        if (onLoad) this.restoreSavedScroll(nav, this.results);
        return true;
      }

      return false;
    }

    scrollToPanelStart(target, behavior = 'smooth') {
      if (!target) return;
      const headerHeight = document.querySelector('.site-header')?.getBoundingClientRect().height || 0;
      const announcementHeight = document.querySelector('.announcement')?.getBoundingClientRect().height || 0;
      const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - headerHeight - announcementHeight - 16);
      window.scrollTo({ top, behavior });
    }

    async restoreCatalogHistory(event) {
      const nav = event?.state && typeof event.state === 'object' ? event.state[this.catalogHistoryField] : null;
      if (!nav) return;
      if (nav.view === 'categories') {
        this.closeDetails({ history: false, scroll: true, scrollBehavior: 'auto' });
        return;
      }
      if (nav.view === 'subgroups' && nav.category) {
        this.selectCategory(nav.category, { history: false, scroll: true, scrollBehavior: 'auto' });
        return;
      }
      if (nav.view === 'results' && nav.category && nav.subgroup) {
        await this.restoreCatalogHistorySnapshot(nav, { onLoad: false });
      }
    }

    showSubgroups(options = {}) {
      if (options.useHistory && this.catalogHistoryValue()?.view === 'results') {
        window.history.back();
        return;
      }
      this.results.hidden = true;
      this.pagination.innerHTML = '';
      this.state.subgroup = null;
      if (this.state.category) {
        this.subgroups.hidden = false;
        if (options.history !== false) this.writeCatalogHistory('subgroups', { replace: true });
        this.scrollToPanelStart(this.subgroups, options.scrollBehavior || 'smooth');
      }
    }

    closeDetails(options = {}) {
      if (options.useHistory && this.catalogHistoryValue()?.view === 'subgroups') {
        window.history.back();
        return;
      }
      this.subgroups.hidden = true;
      this.results.hidden = true;
      this.pagination.innerHTML = '';
      this.state.category = null;
      this.state.subgroup = null;
      if (options.replaceHistory) this.writeCatalogHistory('categories', { replace: true });
      if (options.scroll) this.scrollToPanelStart(this.categories, options.scrollBehavior || 'smooth');
    }

    resetAfter(level) {
      // V18 dependency chain is Make -> Year -> Model -> Power.
      // Reset every field to the RIGHT of the changed field, never the fields before it.
      const resetSelect = (select, label) => {
        select.innerHTML = `<option value="">${label}</option>`;
        select.disabled = true;
        this.searchableSelects.get(select)?.sync();
      };
      if (level === 'make') {
        resetSelect(this.year, 'Select year');
        resetSelect(this.model, 'Select model');
        resetSelect(this.power, 'Select engine power');
        this.modelDataCache.clear();
        Object.assign(this.state, { makeCatalog: null, modelData: null, yearModels: new Map(), modelId: '', year: '', hp: '' });
      } else if (level === 'year') {
        resetSelect(this.model, 'Select model');
        resetSelect(this.power, 'Select engine power');
        Object.assign(this.state, { modelData: null, modelId: '', hp: '' });
      } else if (level === 'model') {
        resetSelect(this.power, 'Select engine power');
        Object.assign(this.state, { modelData: null, hp: '' });
      }
    }

    resetAll() {
      this.make.value = '';
      this.searchableSelects.get(this.make)?.sync();
      this.resetAfter('make');
      Object.assign(this.state, { makeId: '', leaf: null, category: null, subgroup: null });
      this.productMode = 'catalog';
      try { localStorage.removeItem('svaicoVehicle'); } catch (_) {}
      this.closeDetails({ replaceHistory: true });
      this.renderCategories();
      this.setStatus('Showing the full catalogue. Select a vehicle to filter the products.');
      document.dispatchEvent(new CustomEvent('svaico:vehicle-cleared'));
    }

    fill(select, items, placeholder, valueGetter, labelGetter) {
      select.innerHTML = [`<option value="">${escapeHtml(placeholder)}</option>`, ...items.map((item) => `<option value="${escapeHtml(valueGetter(item))}">${escapeHtml(labelGetter(item))}</option>`)].join('');
      select.disabled = items.length === 0;
      this.searchableSelects.get(select)?.sync();
    }

    // V42 MOBILE FITMENT URL: when a phone is showing vehicle-filtered
    // counts, bake the exact vehicle identity directly into each subgroup href.
    // This is deliberately redundant with the click handler: even if browser
    // state/history changes between rendering and tapping, the destination still
    // knows it must render the R2-compatible product set rather than the full
    // Shopify subgroup collection. Desktop keeps the original crawl URL.
    mobileSubgroupHref(categoryName, subgroup, r2 = null) {
      const base = this.crawlCollectionUrl(categoryName, subgroup?.name || '');
      if (!window.matchMedia('(max-width: 749px)').matches || !this.state.leaf || !subgroup) return base;
      const target = new URL(base, window.location.origin);
      target.searchParams.set('view', 'svaico-mobile');
      target.searchParams.set('svaico_category', categoryName || '');
      target.searchParams.set('svaico_subgroup', subgroup.name || '');
      target.searchParams.set('svaico_fitment', '1');
      if (this.state.makeId) target.searchParams.set('svaico_make_id', this.state.makeId);
      if (this.state.modelId) target.searchParams.set('svaico_model_id', this.state.modelId);
      if (this.state.year) target.searchParams.set('svaico_year', this.state.year);
      if (this.state.hp) target.searchParams.set('svaico_hp', this.state.hp);
      const total = Number(r2?.total ?? (Array.isArray(r2?.products) ? r2.products.length : 0));
      if (Number.isFinite(total)) target.searchParams.set('svaico_fitment_total', String(total));
      return target.pathname + '?' + target.searchParams.toString();
    }

    // V26 SEO: stable, ordinary hrefs give crawlers and no-JS/new-tab users
    // a real internal path through category -> subgroup -> product. The fast
    // homepage interaction still intercepts normal clicks above.
    crawlCollectionUrl(categoryTag, subgroupTag = '') {
      const tags = [categoryTag, subgroupTag].map(handleize).filter(Boolean);
      return tags.length ? `/collections/all/${tags.join('+')}` : '/collections/all';
    }

    collectionTagUrl(categoryTag, subgroupTag, view, page = 1) {
      const path = this.crawlCollectionUrl(categoryTag, subgroupTag);
      const params = new URLSearchParams();
      if (view) params.set('view', view);
      if (page > 1) params.set('page', String(page));
      const query = params.toString();
      return query ? `${path}?${query}` : path;
    }

    async runPool(items, concurrency, handler) {
      let cursor = 0;
      const worker = async () => {
        while (cursor < items.length) {
          const index = cursor++;
          try { await handler(items[index], index); } catch (_) {}
        }
      };
      await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
    }

    async fetchJson(relative, withVersion = true) {
      const url = new URL(`${this.baseUrl}/${relative.replace(/^\/+/, '')}`);
      if (withVersion && this.version) url.searchParams.set('v', this.version);
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' }, cache: 'force-cache' });
      if (!response.ok) throw new Error(`${response.status} ${relative}`);
      return response.json();
    }

    setStatus(message, error = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.error = error ? 'true' : 'false';
    }

    fail(error, message) {
      console.error(error);
      this.setStatus(`${message} ${error?.message || ''}`.trim(), true);
    }
  }

  const init = (scope = document) => scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    if (root.dataset.r2Initialized === 'true') return;
    root.dataset.r2Initialized = 'true';
    new R2VehiclePicker(root);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
